import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { selectedStudentIdAtom } from '@atoms/states';
import { useRouter } from 'next/router';
import * as amplitude from '@amplitude/analytics-browser';
import { useEventPropertiesStore } from '@store/common';
import Select, { components } from 'react-select';
import { studentInfoFormSelectCustomStyle } from '@styles/common/formStyles';
import Link from 'next/link';
import { useSelectedStudentInfoQuery, useUserInfoQuery } from '@apis/query/user';
import { useRecoilState } from 'recoil';
import { StudyContentType } from '@asset/enum/materialEnum';
import DropdownActionMenu from '@components/common/DropdownActionMenu';
import * as S from '@styles/styles';
import { Popover } from '@mui/material';
import { useGnbStudentListFilteringQueryStore } from '@store/filteringQueryStore';
import { useInfiniteStudentFilterList } from 'hook/user';
import { StudentStatus } from '@asset/enum/UserEnum';
import { IconAdjustmentsHorizontal, IconCaretDownFilled, IconSearch } from '@tabler/icons-react';
import { Button } from '@components/amc/Button';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export let StudentGroupType = /*#__PURE__*/function (StudentGroupType) {
  StudentGroupType["MY_STUDENTS"] = "my-students";
  StudentGroupType["ALL_STUDENTS"] = "all-students";
  return StudentGroupType;
}({});
const DEBOUNCE_DELAY = 500;
export default function SelectStudentDropdown() {
  const router = useRouter();
  const {
    properties
  } = useEventPropertiesStore();
  const {
    data: user
  } = useUserInfoQuery();
  const {
    data: selectedStudent
  } = useSelectedStudentInfoQuery();
  const {
    filteringInfo,
    setFilteringInfo
  } = useGnbStudentListFilteringQueryStore();
  const [, setSelectedStudentId] = useRecoilState(selectedStudentIdAtom);
  const dropdownAnchorRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [pageType, setPageType] = useState('main');
  const [debouncePendingInputValue, setDebouncePendingInputValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const {
    studentList,
    fetchNextStudentList,
    hasNextStudentList,
    isStudentsLoading
  } = useInfiniteStudentFilterList([], {
    pageSize: 100,
    status: StudentStatus.ENROLLED,
    teacherIds: filteringInfo && filteringInfo[user.id] && filteringInfo[user.id] === StudentGroupType.MY_STUDENTS ? [user.id] : null,
    search: inputValue
  });
  const studentFilterOptions = useMemo(() => studentList.map(student => ({
    value: student.id,
    label: student.name,
    schoolName: student.schoolName
  })), [studentList]);
  const handleLoadMore = useCallback(() => {
    if (!isStudentsLoading && hasNextStudentList) {
      fetchNextStudentList();
    }
  }, [isStudentsLoading, hasNextStudentList, fetchNextStudentList]);
  const handleChangeInput = (newValue, actionMeta) => {
    if (actionMeta.action === 'input-change') {
      setDebouncePendingInputValue(newValue);
    }
  };
  useEffect(() => {
    if (router.isReady) {
      setPageType(router.asPath.startsWith('/manage/dashboard/') ? router.query.type ? router.query?.type.length == 1 ? 'main' : router.query?.type[0] : 'main' : 'main');
    }
  }, [router]);
  useEffect(() => {
    const handler = setTimeout(() => {
      setInputValue(debouncePendingInputValue);
    }, DEBOUNCE_DELAY);
    return () => {
      clearTimeout(handler);
    };
  }, [debouncePendingInputValue]);
  const components = useMemo(() => ({
    Control: props => /*#__PURE__*/_jsx(StudentSelectControl, {
      ...props,
      studentGroupFiltering: filteringInfo && filteringInfo[user.id] ? filteringInfo[user.id] : StudentGroupType.ALL_STUDENTS,
      onSelectFiltering: filtering => setFilteringInfo({
        [user.id]: filtering
      })
    }),
    Option: props => /*#__PURE__*/_jsx(StudentSelectOption, {
      ...props,
      selectedStudent: selectedStudent
    }),
    DropdownIndicator: () => null,
    IndicatorSeparator: () => null,
    LoadingIndicator: () => null
  }), [filteringInfo, user, setFilteringInfo, selectedStudent]);
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsxs("div", {
      className: "flex items-center gap-1 text-label-lg-500 text-my-gray-100",
      ref: dropdownAnchorRef,
      children: [selectedStudent ? /*#__PURE__*/_jsx(Link, {
        href: `/manage/dashboard/main/${selectedStudent.id}`,
        className: "main-student-name",
        children: selectedStudent.name
      }) : /*#__PURE__*/_jsx("span", {
        className: "main-student-name",
        children: "\uD559\uC0DD \uC774\uB984"
      }), /*#__PURE__*/_jsx(Button, {
        variant: "Ghost",
        "aria-label": "toggle menu",
        icon: /*#__PURE__*/_jsx(IconCaretDownFilled, {}),
        size: "Small",
        className: "hover:!bg-zinc-700 active:!bg-zinc-700 [&_svg]:!fill-[var(--gray-100)]",
        onClick: () => {
          setIsOpen(!isOpen);
        }
      })]
    }), /*#__PURE__*/_jsx(Popover, {
      open: isOpen,
      onClose: () => setIsOpen(false),
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left'
      },
      anchorEl: dropdownAnchorRef.current,
      TransitionProps: {
        timeout: 0
      },
      children: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(Select, {
          options: studentFilterOptions,
          placeholder: '학생 검색',
          onChange: async selectedValue => {
            setSelectedStudentId(selectedValue.value);
            router.push(pageType === StudyContentType.MY_STUDY_MATERIAL || pageType === StudyContentType.MY_TEST ? `/manage/dashboard/${pageType}/${selectedValue.value}?isHidden=false` : `/manage/dashboard/${pageType}/${selectedValue.value}`);
            setIsOpen(!isOpen);
            amplitude.track(`click_main_nav_student_list_drop_down`, {
              ...properties,
              studentId: selectedValue?.value
            });
          },
          inputValue: debouncePendingInputValue,
          onInputChange: handleChangeInput,
          components: components,
          onMenuScrollToBottom: handleLoadMore,
          isLoading: isStudentsLoading,
          noOptionsMessage: () => '검색 결과 없음',
          autoFocus: true,
          menuIsOpen: true,
          isSearchable: true,
          backspaceRemovesValue: true,
          filterOption: null,
          closeMenuOnSelect: false,
          styles: studentInfoFormSelectCustomStyle
        })
      })
    })]
  });
}
const StudentSelectControl = props => {
  const {
    children,
    studentGroupFiltering,
    onSelectFiltering,
    ...restProps
  } = props;
  const listContent = {
    [StudentGroupType.MY_STUDENTS]: '나의 학생만 보기',
    [StudentGroupType.ALL_STUDENTS]: '전체 학생 보기'
  };
  return /*#__PURE__*/_jsxs(S.SelectStudentDropdownCustomControl, {
    children: [/*#__PURE__*/_jsxs(components.Control, {
      ...restProps,
      children: [/*#__PURE__*/_jsx(IconSearch, {
        size: 20,
        color: "var(--gray-10)"
      }), children]
    }), /*#__PURE__*/_jsx(DropdownActionMenu, {
      trigger: /*#__PURE__*/_jsx("div", {
        className: "flex items-center",
        children: /*#__PURE__*/_jsx(Button, {
          variant: "Ghost",
          icon: /*#__PURE__*/_jsx(IconAdjustmentsHorizontal, {
            size: 20,
            color: "var(--gray-10)"
          }),
          size: "Medium"
        })
      }),
      items: [{
        label: listContent[StudentGroupType.MY_STUDENTS],
        onClick: () => onSelectFiltering(StudentGroupType.MY_STUDENTS),
        selected: studentGroupFiltering === StudentGroupType.MY_STUDENTS
      }, {
        label: listContent[StudentGroupType.ALL_STUDENTS],
        onClick: () => onSelectFiltering(StudentGroupType.ALL_STUDENTS),
        selected: studentGroupFiltering === StudentGroupType.ALL_STUDENTS
      }],
      contentProps: {
        align: 'end'
      }
    })]
  });
};
const StudentSelectOption = props => {
  const {
    selectedStudent
  } = props;
  return /*#__PURE__*/_jsxs(components.Option, {
    ...props,
    isSelected: selectedStudent?.id === props.data.value,
    children: [props.children, /*#__PURE__*/_jsx("span", {
      children: props.data.schoolName
    })]
  });
};